import React, { memo, useState } from "react";

import { useFormContext } from "react-hook-form";
import { useLocation } from "react-router-dom";

import { Alert, Box, Stack, Text, TextLink } from "@origin-digital/ods-core";
import { IconApartment, IconSme } from "@origin-digital/ods-icons";

import {
  RouterActions,
  StyledHeading,
  TextInput,
  TileButtonGroupInput,
} from "@/components";
import { StyledQuestion } from "@/components/StyledQuestion";
import { useFieldValidation } from "@/hooks";
import { formatLabel } from "@/utils";

import { contactDetails, customerType } from "./content.json";

export const ContactDetail = memo(
  ({
    backRoute,
    nextRoute,
  }: {
    backRoute: () => Promise<string>;
    nextRoute: () => Promise<string>;
  }) => {
    const location = useLocation();
    const passedState = location.state;
    const { register, getValues } = useFormContext();
    const { isValid: isValidContactDetails } = useFieldValidation({
      fields: ["fullName", "email"],
    });
    const { isValid: isValidCustomerType } = useFieldValidation({
      fields: ["customerType"],
    });

    const [selectedCustomerType, setSelectedCustomerType] = useState(
      getValues("customerType") || passedState.data.customerType
    );

    const trackFormUpdate = async () => {
      if (selectedCustomerType === customerType.option.commercial.value) {
        window.location.href = customerType.option.commercial.redirectLink;
        return false;
      } else {
        if (!(await isValidCustomerType())) {
          return false;
        }
        return await isValidContactDetails();
      }
    };

    const icons = [
      <IconSme key="business" />,
      <IconApartment key="commercial" />,
    ];

    const showCustomerType =
      selectedCustomerType !== customerType.residential.value;
    const showCautionMessage =
      selectedCustomerType &&
      selectedCustomerType === customerType.option.commercial.value;
    const showContactDetails =
      selectedCustomerType &&
      (selectedCustomerType === customerType.option.business.value ||
        selectedCustomerType === customerType.residential.value);

    const handleOnChange = (event: string) => {
      setSelectedCustomerType(event);
    };

    const renderCustomerType = () => {
      return (
        <Box paddingTop="none" paddingX="none" marginBottom="xlarge">
          <Stack space="medium" data-id="customer-type">
            <StyledHeading>{customerType.heading}</StyledHeading>
            <TileButtonGroupInput
              cols={2}
              options={customerType.option}
              icons={icons}
              handleOnChange={handleOnChange}
              {...register("customerType")}
            />
          </Stack>
        </Box>
      );
    };

    return (
      <StyledQuestion>
        {showCustomerType && <>{renderCustomerType()}</>}
        {showCautionMessage && (
          <Alert tone="caution">
            <Text data-id={customerType.cautionMessageId}>
              {customerType.cautionMessage}
            </Text>
          </Alert>
        )}
        {showContactDetails && (
          <>
            <div>
              <StyledHeading>{contactDetails.heading}</StyledHeading>
              <Text wordBreak={true}>{contactDetails.description}</Text>
              <br />
              <TextInput
                id={contactDetails.fullName.id}
                data-id={contactDetails.fullName.id}
                label={contactDetails.fullName.label}
                {...register("fullName")}
              />
              <p />
              <TextInput
                id={contactDetails.email.id}
                data-id={contactDetails.email.id}
                label={contactDetails.email.label}
                {...register("email")}
              />
            </div>
            <br />
            <Text>
              {formatLabel(
                contactDetails.note,
                (value) => (
                  <TextLink
                    href={contactDetails.contactLink.url}
                    target="_blank"
                  >
                    {value}
                  </TextLink>
                ),
                contactDetails.contactLink.text
              )}
            </Text>
          </>
        )}
        <RouterActions
          onBackRoute={backRoute}
          onNextRoute={nextRoute}
          onNext={trackFormUpdate}
        />
      </StyledQuestion>
    );
  }
);
